/**
 * TopicBarContext — State management for the top-level topic tab bar.
 *
 * Tracks open topics, focused topic, background mode, and persists
 * open tab IDs to localStorage. Fetches topic data from the API on mount.
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import { topicsApi, type Topic, type WorkpieceType } from '../api/topics';
import { useWorkspace } from './WorkspaceContext';
import { broadcastTopicEvent, useTopicSyncListener } from '../hooks/useTopicSync';

// ── Storage ────────────────────────────────────────────────────────────────────

const STORAGE_KEY = 'martha.topicbar.open';
const FOCUSED_KEY = 'martha.topicbar.focused';

function readOpenIds(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function writeOpenIds(ids: string[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch { /* noop */ }
}

function readFocusedId(): string | null {
  try {
    return localStorage.getItem(FOCUSED_KEY) || null;
  } catch {
    return null;
  }
}

function writeFocusedId(id: string | null) {
  try {
    if (id) localStorage.setItem(FOCUSED_KEY, id);
    else localStorage.removeItem(FOCUSED_KEY);
  } catch { /* noop */ }
}

// ── Context shape ──────────────────────────────────────────────────────────────

interface TopicBarContextValue {
  /** All topics in the current workspace */
  allTopics: Topic[];
  /** Topics currently open as tabs */
  openTopics: Topic[];
  /** Currently focused/active topic (full width view) */
  focusedTopicId: string | null;
  /** The focused topic object (convenience) */
  focusedTopic: Topic | null;
  /** Whether the topic bar has any open topics */
  hasOpenTopics: boolean;
  /** Loading state */
  isLoading: boolean;
  /** ID of a just-created topic (for auto-entering edit mode) */
  newlyCreatedId: string | null;

  /** Open a topic tab (adds to bar, does NOT focus) */
  addTopic: (topicOrId: Topic | string) => void;
  /** Close a topic tab */
  removeTopic: (id: string) => void;
  /** Focus a topic (replaces main content), or null to return to pane view */
  focusTopic: (id: string | null) => void;
  /** Toggle background mode for a topic */
  setBackground: (id: string, bg: boolean) => void;
  /** Update local topic fields (optimistic) */
  updateTopic: (id: string, partial: Partial<Topic>) => void;
  /** Create and open a new topic */
  createTopic: (input: {
    type?: string;
    title: string;
    description?: string;
    object_type?: string;
    object_id?: string;
    workpiece_type?: WorkpieceType;
    workpiece_id?: string | null;
    assigned_agents?: string[];
  }) => Promise<Topic | null>;
  /** Clear the newly-created flag (called after TopicTab enters edit mode) */
  clearNewlyCreated: () => void;
  /** Refetch topics from API */
  refetch: () => void;
}

const TopicBarContext = createContext<TopicBarContextValue | null>(null);

// ── Provider ───────────────────────────────────────────────────────────────────

export function TopicBarProvider({ children }: { children: ReactNode }) {
  const { currentWorkspace } = useWorkspace();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();

  // Gate topic fetches on URL slug match — prevents fetching for stale
  // localStorage workspace before WorkspaceRouter syncs to the URL.
  const urlSlugMatch = location.pathname.match(/^\/w\/([^/]+)/);
  const urlSlug = urlSlugMatch?.[1] || null;
  const wsSlug = currentWorkspace?.slug ?? '';
  const workspaceId = (!urlSlug || wsSlug === urlSlug) ? wsSlug : '';

  // Read initial focused topic from URL — supports both:
  //   ?topic=<id>  (new — preserves underlying page context)
  //   /w/:ws/to/<id> (legacy — still works, redirected on focus)
  const searchParams = new URLSearchParams(location.search);
  const legacyMatch = location.pathname.match(/\/w\/[^/]+\/to\/([a-z0-9]{6,7})/);
  const urlTopicId = searchParams.get('topic') ?? legacyMatch?.[1] ?? null;

  // Persisted state
  const [openIds, setOpenIds] = useState<string[]>(readOpenIds);
  // Only initialize from URL — never from localStorage. Reading from localStorage
  // caused every non-topic route to be hijacked on page load.
  const [focusedTopicId, setFocusedTopicId] = useState<string | null>(urlTopicId);
  const [newlyCreatedId, setNewlyCreatedId] = useState<string | null>(null);
  const fetchingIdRef = useRef<string | null>(null);
  // Topics fetched individually (not in the limited list). Survives list refetches.
  const [pinnedTopics, setPinnedTopics] = useState<Topic[]>([]);

  // Fetch recent workspace topics — only when workspace matches URL.
  // Limited to 100 to avoid multi-MB responses (workspace can have thousands).
  // Individual topics not in this list are fetched on-demand in focusTopic().
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['topics', workspaceId],
    queryFn: () => topicsApi.list({ workspace_id: workspaceId, limit: 100 }),
    enabled: !!workspaceId,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  // Merge list topics with pinned individually-fetched topics.
  // Pinned versions take precedence: the list endpoint strips `context`
  // (and therefore `tool_results`) for payload size, so the canvas-derived
  // cards (drafts, status, derived next actions) only show up when we use
  // the full single-topic payload.
  const allTopics = useMemo(() => {
    const listTopics = data?.topics ?? [];
    const pinnedById = new Map(pinnedTopics.map(t => [t.id, t]));
    const merged = listTopics.map(lt => pinnedById.get(lt.id) ?? lt);
    const extras = pinnedTopics.filter(pt => !listTopics.some(lt => lt.id === pt.id));
    return extras.length > 0 ? [...merged, ...extras] : merged;
  }, [data, pinnedTopics]);

  // Fetch any open/focused topics that are not in the limited list response
  const fetchedMissingRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (isLoading || !data) return;
    const knownIds = new Set(allTopics.map(t => t.id));
    const missingIds = openIds.filter(id => !knownIds.has(id) && !fetchedMissingRef.current.has(id));
    if (focusedTopicId && !knownIds.has(focusedTopicId) && !fetchedMissingRef.current.has(focusedTopicId)) {
      missingIds.push(focusedTopicId);
    }
    for (const id of missingIds) {
      fetchedMissingRef.current.add(id);
      // Guard the unfocus effect so it doesn't clear focusedTopicId while this fetch is in flight
      if (fetchingIdRef.current === null) fetchingIdRef.current = id;
      topicsApi.get(id).then(({ topic }) => {
        // Pin the topic so it survives list refetches (list is capped at 100)
        setPinnedTopics(prev => prev.some(t => t.id === id) ? prev : [...prev, topic]);
      }).catch(() => {}).finally(() => {
        if (fetchingIdRef.current === id) fetchingIdRef.current = null;
      });
    }
  }, [isLoading, data, openIds, focusedTopicId, allTopics, workspaceId]);

  // Always fetch the focused topic individually — even when it's already in
  // the list response — because the list strips `context.tool_results`, which
  // the artifact-board canvas needs to render comms drafts, status cards, and
  // derived next actions. The pinned version then overrides the list version
  // via the merge above.
  useEffect(() => {
    if (!focusedTopicId) return;
    let cancelled = false;
    topicsApi.get(focusedTopicId)
      .then(({ topic }) => {
        if (cancelled) return;
        setPinnedTopics(prev => {
          const without = prev.filter(t => t.id !== focusedTopicId);
          return [...without, topic];
        });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [focusedTopicId]);

  // Derive open topics (preserve tab order from openIds)
  const openTopics = useMemo(() => {
    const byId = new Map(allTopics.map(t => [t.id, t]));
    return openIds.map(id => byId.get(id)).filter(Boolean) as Topic[];
  }, [allTopics, openIds]);

  const focusedTopic = useMemo(
    () => allTopics.find(t => t.id === focusedTopicId) ?? null,
    [allTopics, focusedTopicId],
  );

  // Persist open IDs
  useEffect(() => { writeOpenIds(openIds); }, [openIds]);
  useEffect(() => { writeFocusedId(focusedTopicId); }, [focusedTopicId]);

  // Sync focused topic to URL via ?topic= query param.
  // This preserves the underlying page path so shared URLs encode both contexts.
  const [navIntent, setNavIntent] = useState<{ id: string | null; ts: number } | null>(null);

  // Use a ref so the effect can read current search params without location.search
  // being a dependency. Having location.search as a dep caused an infinite loop:
  // navigate() updates location.search → effect re-runs → navigate() again...
  const locationSearchRef = useRef(location.search);
  locationSearchRef.current = location.search;

  useEffect(() => {
    if (!navIntent) return;
    const wsSlug = currentWorkspace?.slug ?? 'archie';

    // If we're on a legacy /to/:id route, redirect to the workspace root with ?topic=
    const onLegacyRoute = /\/w\/[^/]+\/to\/[a-z0-9]{6,7}/.test(location.pathname);
    const basePath = onLegacyRoute ? `/w/${wsSlug}` : location.pathname;

    const currentSearch = locationSearchRef.current;
    if (navIntent.id) {
      const params = new URLSearchParams(currentSearch);
      params.set('topic', navIntent.id);
      navigate(`${basePath}?${params.toString()}`, { replace: true });
    } else {
      const params = new URLSearchParams(currentSearch);
      params.delete('topic');
      const qs = params.toString();
      navigate(`${basePath}${qs ? `?${qs}` : ''}`, { replace: true });
    }
    setNavIntent(null);
  }, [navIntent, navigate, currentWorkspace, location.pathname]); // location.search removed — use ref above

  // If URL topic ID exists on load, auto-open it
  useEffect(() => {
    if (urlTopicId && !openIds.includes(urlTopicId)) {
      setOpenIds(prev => prev.includes(urlTopicId) ? prev : [...prev, urlTopicId]);
    }
  }, []);

  // If focused topic was removed from API, unfocus
  useEffect(() => {
    if (focusedTopicId && !allTopics.some(t => t.id === focusedTopicId)) {
      if (fetchingIdRef.current === focusedTopicId) return; // fetch in flight — wait for it
      // Only clear if we've actually loaded data (not during initial load)
      if (!isLoading && allTopics.length > 0) {
        setFocusedTopicId(null);
      }
    }
  }, [focusedTopicId, allTopics, isLoading]);

  // When the user navigates to a different page (e.g. sidebar click) while a
  // topic is focused, unfocus the topic so the destination page is visible.
  // Only watches pathname — search changes (from navIntent) should not trigger.
  useEffect(() => {
    if (!focusedTopicId) return;
    const params = new URLSearchParams(location.search);
    const onLegacyRoute = /\/w\/[^/]+\/to\/[a-z0-9]{6,7}/.test(location.pathname);
    if (!params.has('topic') && !onLegacyRoute) {
      // User navigated without ?topic= → they want to leave the topic
      setFocusedTopicId(null);
      writeFocusedId(null);
    }
  }, [location.pathname]); // intentionally only pathname — search changes from navIntent should not trigger

  // ── Actions ──────────────────────────────────────────────────────────────

  const addTopic = useCallback((topicOrId: Topic | string) => {
    const id = typeof topicOrId === 'string' ? topicOrId : topicOrId.id;
    setOpenIds(prev => prev.includes(id) ? prev : [...prev, id]);
  }, []);

  const removeTopic = useCallback((id: string) => {
    setOpenIds(prev => prev.filter(x => x !== id));
    setFocusedTopicId(prev => {
      if (prev === id) {
        // Removing the focused topic — clean up ?topic from URL
        setNavIntent({ id: null, ts: Date.now() });
        return null;
      }
      return prev;
    });
  }, []);

  const focusTopic = useCallback((id: string | null) => {
    if (id) {
      // Auto-add to open tabs when focusing
      setOpenIds(prev => prev.includes(id) ? prev : [...prev, id]);
      // If topic isn't in current workspace cache (e.g. found via global search),
      // fetch it individually and inject — prevents the unfocus effect from
      // immediately clearing focusedTopicId before the cache catches up.
      const cached = queryClient.getQueryData<{ topics: Topic[] }>(['topics', workspaceId]);
      if (!cached?.topics.some(t => t.id === id)) {
        fetchingIdRef.current = id;
        topicsApi.get(id)
          .then(({ topic }) => {
            setPinnedTopics(prev => prev.some(t => t.id === id) ? prev : [...prev, topic]);
          })
          .catch(() => {})
          .finally(() => {
            if (fetchingIdRef.current === id) fetchingIdRef.current = null;
          });
      }
    }
    setFocusedTopicId(id);
    setNavIntent({ id, ts: Date.now() });
    broadcastTopicEvent({ type: 'topic:focused', topicId: id });
  }, [queryClient, workspaceId]);

  const setBackground = useCallback(async (id: string, bg: boolean) => {
    await topicsApi.setBackground(id, bg);
    queryClient.invalidateQueries({ queryKey: ['topics'] });
    queryClient.invalidateQueries({ queryKey: ['topic', id] });
  }, [queryClient]);

  const updateTopic = useCallback((id: string, partial: Partial<Topic>) => {
    // Update list cache
    queryClient.setQueryData(
      ['topics', workspaceId],
      (old: { topics: Topic[] } | undefined) => {
        if (!old) return old;
        return { topics: old.topics.map(t => t.id === id ? { ...t, ...partial } : t) };
      },
    );
    // Update individual topic cache (used by TopicPage)
    queryClient.setQueryData(
      ['topic', id],
      (old: { topic: Topic } | undefined) => old ? { topic: { ...old.topic, ...partial } } : old,
    );
    // Update pinned topics (individually-fetched topics not in list)
    setPinnedTopics(prev => prev.map(t => t.id === id ? { ...t, ...partial } : t));
  }, [queryClient, workspaceId]);

  const createTopic = useCallback(async (input: {
    type?: string;
    title: string;
    description?: string;
    object_type?: string;
    object_id?: string;
    workpiece_type?: WorkpieceType;
    workpiece_id?: string | null;
    assigned_agents?: string[];
  }): Promise<Topic | null> => {
    if (!workspaceId) return null;
    const { topic } = await topicsApi.create({ ...input, workspace_id: workspaceId });
    // Optimistically inject into cache so focusedTopic resolves immediately
    queryClient.setQueryData(
      ['topics', workspaceId],
      (old: { topics: Topic[] } | undefined) => {
        if (!old) return { topics: [topic] };
        return { topics: [...old.topics, topic] };
      },
    );
    // Add to open tabs, focus, and update URL
    setOpenIds(prev => [...prev, topic.id]);
    setFocusedTopicId(topic.id);
    setNewlyCreatedId(topic.id);
    setNavIntent({ id: topic.id, ts: Date.now() });
    queryClient.invalidateQueries({ queryKey: ['topics'] });
    broadcastTopicEvent({ type: 'topic:created', topicId: topic.id });
    return topic;
  }, [workspaceId, queryClient]);

  const clearNewlyCreated = useCallback(() => setNewlyCreatedId(null), []);

  // ── Cross-window sync ─────────────────────────────────────────────────────

  useTopicSyncListener(useCallback((msg) => {
    switch (msg.type) {
      case 'topic:created':
      case 'topic:updated':
        queryClient.invalidateQueries({ queryKey: ['topics'] });
        break;
      case 'topic:removed':
        setOpenIds(prev => prev.filter(x => x !== msg.topicId));
        setFocusedTopicId(prev => prev === msg.topicId ? null : prev);
        break;
    }
  }, [queryClient]));

  // ── Value ────────────────────────────────────────────────────────────────

  const value = useMemo<TopicBarContextValue>(() => ({
    allTopics,
    openTopics,
    focusedTopicId,
    focusedTopic,
    hasOpenTopics: openTopics.length > 0,
    isLoading,
    newlyCreatedId,
    addTopic,
    removeTopic,
    focusTopic,
    setBackground,
    updateTopic,
    createTopic,
    clearNewlyCreated,
    refetch,
  }), [
    allTopics, openTopics, focusedTopicId, focusedTopic,
    isLoading, newlyCreatedId, addTopic, removeTopic, focusTopic,
    setBackground, updateTopic, createTopic, clearNewlyCreated, refetch,
  ]);

  return (
    <TopicBarContext.Provider value={value}>
      {children}
    </TopicBarContext.Provider>
  );
}

// ── Hook ───────────────────────────────────────────────────────────────────────

// No-op fallback for pop-out windows (no TopicBarProvider in tree)
const POPOUT_FALLBACK: TopicBarContextValue = {
  allTopics: [],
  openTopics: [],
  focusedTopicId: null,
  focusedTopic: null,
  hasOpenTopics: false,
  isLoading: false,
  newlyCreatedId: null,
  addTopic: () => {},
  removeTopic: () => {},
  focusTopic: () => {},
  setBackground: () => {},
  updateTopic: () => {},
  createTopic: async () => null,
  clearNewlyCreated: () => {},
  refetch: () => {},
};

export function useTopicBar(): TopicBarContextValue {
  const ctx = useContext(TopicBarContext);
  if (!ctx) return POPOUT_FALLBACK; // Pop-out window — no provider
  return ctx;
}
